import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Alert, IconButton, Grid, Chip
} from '@mui/material';
import { Add, Edit, Delete, Send } from '@mui/icons-material';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/apiHelpers';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ email: '', password: '', role: 'user', is_active: true, full_name: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({ fullName: '', email: '', role: '', status: '', inviteStatus: '' });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await apiGet('/api/users');
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        setError('Session expired. Please login again.');
        localStorage.removeItem('token');
      } else {
        setError('Failed to fetch users');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingUser) {
        await apiPut(`/api/users/${editingUser.user_id}`, formData);
        setSuccess('User updated successfully');
      } else {
        const response = await apiPost('/api/users', formData);
        const emailStatus = response.data?.emailSent ? ' Login credentials have been sent to the user\'s email.' : ' Note: Email notification could not be sent.';
        setSuccess('User created successfully.' + emailStatus);
      }

      fetchUsers();
      handleClose();
    } catch (error) {
      if (error.response?.status === 401) {
        setError('Session expired. Please login again.');
        localStorage.removeItem('token');
      } else {
        setError(error.response?.data?.error || 'Operation failed');
      }
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await apiDelete(`/api/users/${userId}`);
        setSuccess('User deleted successfully');
        fetchUsers();
      } catch (error) {
        if (error.response?.status === 401) {
          setError('Session expired. Please login again.');
          localStorage.removeItem('token');
        } else {
          setError('Failed to delete user');
        }
      }
    }
  };

  const handleResendCredentials = async (userId, userEmail) => {
    if (window.confirm(`Resend login credentials to ${userEmail}?\n\nThis will generate a new temporary password valid for 24 hours.`)) {
      try {
        const response = await apiPost(`/api/users/${userId}/resend-credentials`);
        setSuccess('Credentials resent successfully. User will receive an email with new login details.');
        fetchUsers();
      } catch (error) {
        if (error.response?.status === 401) {
          setError('Session expired. Please login again.');
          localStorage.removeItem('token');
        } else {
          setError(error.response?.data?.error || 'Failed to resend credentials');
        }
      }
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      role: user.role,
      is_active: user.is_active,
      full_name: user.full_name || ''
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingUser(null);
    setFormData({ email: '', password: '', role: 'user', is_active: true, full_name: '' });
    setError('');
  };

  const getUserStatus = (user) => {
    if (!user.is_active) {
      return { label: 'Disabled', color: 'default', type: 'disabled' };
    }
    if (user.must_change_password) {
      const now = new Date();
      const expiresAt = user.temp_password_expires_at ? new Date(user.temp_password_expires_at) : null;
      if (expiresAt && now > expiresAt) {
        return { label: 'Expired', color: 'error', type: 'expired' };
      }
      return { label: 'Invited', color: 'warning', type: 'invited' };
    }
    return { label: 'Active', color: 'success', type: 'active' };
  };

  const getExpirationInfo = (user) => {
    if (!user.must_change_password || !user.temp_password_expires_at) return '-';
    
    const now = new Date();
    const expiresAt = new Date(user.temp_password_expires_at);
    const diffMs = expiresAt - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMs < 0) {
      const expiredDays = Math.abs(diffDays);
      return expiredDays > 0 ? `Expired ${expiredDays}d ago` : 'Expired today';
    }
    
    if (diffDays > 0) {
      return `${diffDays}d ${diffHours % 24}h left`;
    }
    return `${diffHours}h left`;
  };

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesFullName = !filters.fullName || (user.full_name || '').toLowerCase().includes(filters.fullName.toLowerCase());
      const matchesEmail = !filters.email || user.email.toLowerCase().includes(filters.email.toLowerCase());
      const matchesRole = !filters.role || user.role === filters.role;
      const matchesStatus = !filters.status || (filters.status === 'active' ? user.is_active : !user.is_active);
      
      // Invite status filter
      let matchesInviteStatus = true;
      if (filters.inviteStatus) {
        const status = getUserStatus(user);
        matchesInviteStatus = status.type === filters.inviteStatus;
      }
      
      return matchesFullName && matchesEmail && matchesRole && matchesStatus && matchesInviteStatus;
    });
  }, [users, filters]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">User Management</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={filters.inviteStatus}
              onChange={(e) => setFilters({ ...filters, inviteStatus: e.target.value })}
              label="Filter by Status"
            >
              <MenuItem value="">All Users</MenuItem>
              <MenuItem value="active">🟢 Active</MenuItem>
              <MenuItem value="invited">🟡 Pending Invites</MenuItem>
              <MenuItem value="expired">🔴 Expired Invites</MenuItem>
              <MenuItem value="disabled">⚫ Disabled</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setOpen(true)}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600, width: '15%' }}>Full Name</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '18%' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '12%' }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '12%' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '12%' }}>Expires In</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '13%' }}>Created At</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '12%' }}>Actions</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ py: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Full Name...."
                  value={filters.fullName}
                  onChange={(e) => setFilters({ ...filters, fullName: e.target.value })}
                  sx={{ bgcolor: 'white' }}
                />
              </TableCell>
              <TableCell sx={{ py: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Email..."
                  value={filters.email}
                  onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                  sx={{ bgcolor: 'white' }}
                />
              </TableCell>
              <TableCell sx={{ py: 1 }}>
                <Select
                  fullWidth
                  size="small"
                  value={filters.role}
                  onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                  displayEmpty
                  sx={{ bgcolor: 'white' }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="account_onboarding">Account Onboarding</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </TableCell>
              <TableCell sx={{ py: 1 }}>
                <Select
                  fullWidth
                  size="small"
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  displayEmpty
                  sx={{ bgcolor: 'white' }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="disabled">Disabled</MenuItem>
                </Select>
              </TableCell>
              <TableCell sx={{ py: 1 }} />
              <TableCell sx={{ py: 1 }} />
              <TableCell sx={{ py: 1 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => {
              const status = getUserStatus(user);
              const expirationInfo = getExpirationInfo(user);
              const isExpired = status.type === 'expired';
              
              return (
              <TableRow 
                key={user.user_id} 
                hover 
                sx={{ 
                  '&:hover': { bgcolor: 'action.hover' },
                  bgcolor: isExpired ? 'error.lighter' : 'inherit'
                }}
              >
                <TableCell>{user.full_name || '-'}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.role === 'account_onboarding' ? 'Account Onboarding' : user.role.charAt(0).toUpperCase() + user.role.slice(1)}</TableCell>
                <TableCell>
                  <Chip 
                    label={status.label} 
                    color={status.color} 
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    color={isExpired ? 'error.main' : 'text.secondary'}
                    sx={{ fontWeight: isExpired ? 600 : 400 }}
                  >
                    {expirationInfo}
                  </Typography>
                </TableCell>
                <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(user)} color="primary" size="small" title="Edit User">
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(user.user_id)} color="error" size="small" title="Delete User">
                    <Delete fontSize="small" />
                  </IconButton>
                  {user.must_change_password && (
                    <IconButton 
                      onClick={() => handleResendCredentials(user.user_id, user.email)} 
                      color="success" 
                      size="small"
                      title="Resend Credentials"
                    >
                      <Send fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            <TextField
              fullWidth
              label="Full Name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              margin="normal"
            />
            
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              label={editingUser ? "New Password (leave blank to keep current)" : "Password"}
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              margin="normal"
              required={!editingUser}
              helperText="Must be 14-22 characters with uppercase, lowercase, number, and symbol"
            />
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                label="Role"
              >
                <MenuItem value="user">User (Read Only)</MenuItem>
                <MenuItem value="account_onboarding">Account Onboarding</MenuItem>
                <MenuItem value="admin">Admin (Full Access)</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                label="Status"
              >
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Disabled</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="outlined">
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default UserManagement;